//dependencies
import { useLocation } from "react-router-dom";

//hooks
import { useCurrentUser, useUser } from "../../api/hooks";

//utils
import {
	parseEmployeeDashboardOrigin,
	parseEmployeeDashboardOriginDeep,
} from "../../utils/employeeDashboardBreadcrumbs";

export type EmployeeDashboardOriginResult =
	/** The current URL carries no other-employee dashboard origin. */
	| { status: "none" }
	/** A dashboard origin is present but the current user or employee name hasn't resolved yet. */
	| { status: "loading" }
	| {
			status: "resolved";
			/** Sanitized internal `from` path back to the originating dashboard. */
			from: string;
			/** The employee whose dashboard the user came from. */
			employeeId: string;
			/** Display name of that employee. */
			employeeName: string;
			/** Intermediate list-page href for deep origins (dashboard → list → detail); null when direct. */
			viaHref: string | null;
	  };

/**
 * Resolve an another-employee dashboard origin from the current URL, including the employee's
 * display name. Consumers should render nothing origin-specific while `status` is "loading" so
 * the crumb doesn't flash a generic label before the name resolves. Pass `deep: true` to also
 * recognize origins nested inside an intermediate page's `from`.
 */
export const useEmployeeDashboardOrigin = ({
	deep = false,
}: { deep?: boolean } = {}): EmployeeDashboardOriginResult => {
	const location = useLocation();
	const { data: me } = useCurrentUser();
	const currentUserId = me?.me?.authorization?.payload?.user_id || "";

	// Pattern match without the self-exclusion first, so "no origin at all" resolves to
	// "none" immediately instead of waiting on the current user.
	const candidate = deep
		? parseEmployeeDashboardOriginDeep(location.search)
		: parseEmployeeDashboardOrigin(location.search);
	const { data: employee, isError } = useUser({
		userID: candidate?.employeeId ?? "",
		disabled: !candidate || !currentUserId || candidate.employeeId === currentUserId,
	});

	if (!candidate) return { status: "none" };
	if (!currentUserId) return { status: "loading" };
	if (candidate.employeeId === currentUserId) return { status: "none" };
	// The employee isn't resolvable (e.g. not visible to this viewer): fall back to generic crumbs.
	if (isError) return { status: "none" };
	if (!employee?.name) return { status: "loading" };
	return {
		status: "resolved",
		from: candidate.from,
		employeeId: candidate.employeeId,
		employeeName: employee.name,
		viaHref: "viaHref" in candidate ? candidate.viaHref : null,
	};
};
